import { ApplicationRef, ComponentFactory, ComponentFactoryResolver, Injectable, Type } from "@angular/core";
import { ModalContainerComponent } from "../components/modal-container/modal-container.component";
import { ModalInputs, ModalNames, Modals } from "../modals/v2/Modal";
import { ModalRef } from "../models/ModalRef.model";

@Injectable()
export class ModalService2 {

    private modalContainer: HTMLElement;
    private modalContainerFactory: ComponentFactory<ModalContainerComponent>;
    private modalStack: ModalRef[] = [];
    
    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef
    ) {
        this.setupModalContainerFactory();
    }

    open<T extends ModalNames>(modalName: T, inputs?: ModalInputs[T]|undefined) {
        if (this.isOpenOfType(modalName)) {
            this.closeOfType(modalName);
        }

        this.setupModalContainerDiv();

        const modalContainerRef = this.appRef.bootstrap(this.modalContainerFactory, this.modalContainer);

        const component = Modals[modalName];
        const modalComponentRef = modalContainerRef.instance.createModal(component);

        if (inputs) {
            modalComponentRef.instance.onInjectInputs(inputs);
        }

        const modalRef = new ModalRef(modalContainerRef, modalComponentRef);
        this.modalStack.push(modalRef);

        return modalRef.asPromise();
    }

    getOpenOfType(modalName: ModalNames): ModalRef|undefined {
        return this.modalStack.find(x => x.getModal().instance.getName() === modalName);   
    }

    isOpenOfType(modalName: ModalNames) {
        return this.getOpenOfType(modalName) !== undefined;
    }

    closeOfType(modalName: ModalNames): void {
        this.getOpenOfType(modalName)?.close('force');
    }

    getOpenModal(): ModalRef|null {
        if (!this.hasOpenModal) return null;
        return this.modalStack[this.modalStack.length - 1];
    }

    hasOpenModal(): boolean {
        return this.modalStack.length > 0;
    }

    dismissLast(): void {
        const lastModal = this.modalStack.pop();
        lastModal?.dismiss();
    }

    closeLast(output?: any): void {
        const lastModal = this.modalStack.pop();
        lastModal?.close(output);
    }

    dismissAll(): void {
        while(this.modalStack.length > 0) {
            this.dismissLast();
        }
    }

    closeAll(output?: any): void {
        while(this.modalStack.length > 0) {
            this.closeLast(output);
        }
    }

    onDimmerClick(): void {
        const lastModal = this.getOpenModal();
        const instance = lastModal?.getModal().instance;
        if (!instance || !instance.allowDimmerClose()) return;
        this.dismissLast();
    }

    private setupModalContainerDiv(): void {
        this.modalContainer = document.createElement('div');
        document.getElementsByTagName('body')[0].appendChild(this.modalContainer);
    }

    private setupModalContainerFactory(): void {
        this.modalContainerFactory = this.componentFactoryResolver.resolveComponentFactory(ModalContainerComponent);
    }

}
